'use client'

import { Icon, Image } from '@/components'
import { Fancybox } from '@fancyapps/ui'
import '@fancyapps/ui/dist/fancybox/fancybox.css'
import { Box, IconButton, styled } from '@mui/material'
import { Pagination } from 'swiper/modules'
import { Swiper, SwiperSlide } from 'swiper/react'

export const StyledPagination = styled(Box)(({ theme }) => ({
  position: 'static',
  textAlign: 'center',
  margin: '2px auto 0',
  opacity: 1,
  maxHeight: '100%',
  lineHeight: 1,
  '& .swiper-pagination-bullet': {
    width: '8px',
    height: '8px',
    background: 'transparent',
    border: `1px solid ${theme.palette.primary.main}`,
    opacity: 1,
    margin: '0 2px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    lineHeight: 1,
    '&.swiper-pagination-bullet-active': {
      background: theme.palette.primary.main,
      width: '24px',
      borderRadius: '999px',
    },
  },
  [theme.breakpoints.up('lg')]: {
    opacity: 0,
    maxHeight: 0,
  },
}))

// Create a unified interface for both types
interface ListItem {
  id: number
  type: 'video' | 'image'
  src?: string
  html?: string
  caption?: string
  preload?: boolean
}

interface IReviewMedia {
  media_id: number
  type: string
  url: string
  thumbnail: string
}

type ListImagesReviewProps =
  | { media: IReviewMedia[]; photos?: never; videos?: never }
  | { photos: Array<string>; videos: Array<string>; media?: never }

export const ListImagesReview = ({ media, photos, videos }: ListImagesReviewProps) => {
  // Tạo danh sách kết hợp video và image từ media array (API mới) hoặc photos/videos (legacy)
  const list: ListItem[] = media
    ? media.map((item, index) => ({
        id: index,
        type: item.type === 'video' ? ('video' as const) : ('image' as const),
        src: item.url,
      }))
    : [
        ...(videos || []).map((item, index) => ({
          id: index,
          type: 'video' as const,
          src: item,
        })),
        ...(photos || []).map((item, index) => ({
          id: index + (videos?.length || 0),
          type: 'image' as const,
          src: item,
        })),
      ]
  // id="fancybox-video-${item.id}"
  // Xử lý click để mở Fancybox
  const handleItemClick = (clickedIndex: number) => {
    const fancyboxItems = list.map((item) => {
      if (item.type === 'video') {
        return {
          src: item.src,
          type: 'html' as const,
          thumb: item.src, // Sử dụng video URL làm thumbnail (sẽ lấy frame đầu tiên)
          html: `
  <div style="position: relative; width: 100%; max-width: 1200px; margin: 0 auto;">
    <video 
      controls 
      playsinline
      preload="auto"
      style="
        width: 100%;
        height: auto;
        max-height: 85vh;
        display: block;
      "
    >
      <source src="${item.src}" type="video/mp4">
      Your browser does not support the video tag.
    </video>
   
  </div>
  <script>
    (function() {
      const video = document.getElementById('fancybox-video-${item.id}');
      const overlay = document.getElementById('play-overlay-${item.id}');
      
      video.addEventListener('play', () => {
        overlay.style.display = 'none';
      });
      
      video.addEventListener('pause', () => {
        if (!video.ended) {
          overlay.style.display = 'flex';
        }
      });
      
      // Try autoplay
      video.play().catch(() => {
        // Autoplay failed, show overlay
        overlay.style.display = 'flex';
      });
    })();
  </script>
`,
        }
      } else {
        return {
          src: item.src,
          type: 'image' as const,
          thumb: item.src, // Thumbnail cho image
        }
      }
    })

    Fancybox.show(fancyboxItems, {
      startIndex: clickedIndex,
      dragToClose: false,
      mainClass: 'fancybox-custom',
      infinite: false,
      autoFocus: false,
      // Cấu hình cho background
      backdropClick: 'close',
      closeButton: false,
      // Cấu hình thumbs
      Thumbs: {
        type: 'classic',
      },
    } as any)
  }

  return (
    <Box sx={{ marginTop: 'auto' }}>
      <Box
        sx={{
          '& .swiper-slide': {
            width: 'auto !important',
            height: { xs: '85px', sm: '105px', md: '120px' },
          },
        }}
      >
        <Swiper
          modules={[Pagination]}
          pagination={{
            clickable: true,
            type: 'bullets',
            el: '.swiper-pagination-custom',
          }}
          spaceBetween={8}
          slidesPerView={'auto'}
          style={{ overflow: 'visible' }}
          freeMode={true}
        >
          {list?.map((item, index) => (
            <SwiperSlide
              key={item?.id}
              style={{
                aspectRatio: '4/3',
                width: 'auto',
              }}
            >
              <Box
                onClick={() => handleItemClick(index)}
                sx={{
                  width: '100%',
                  height: '100%',
                  borderRadius: '8px',
                  overflow: 'hidden',
                  cursor: 'pointer',
                  position: 'relative',
                  '&:hover': {
                    opacity: 0.8,
                    transition: 'opacity 0.2s ease',
                  },
                }}
              >
                {item.type === 'video' ? (
                  // Video thumbnail với play icon
                  <>
                    <Box
                      component="video"
                      src={`${item.src}#t=0.1`}
                      muted
                      preload="metadata"
                      playsInline
                      webkit-playsinline="true" // Add this for older iOS
                      x-webkit-airplay="allow" // Add this
                      sx={{
                        borderRadius: '8px',
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        pointerEvents: 'none',
                        backgroundColor: '#BBBBBA',
                      }}
                      onLoadedData={(e) => {
                        // Changed from onLoadedMetadata
                        const video = e.target as HTMLVideoElement
                        if (video.readyState >= 2) {
                          // HAVE_CURRENT_DATA
                          video.currentTime = 0.1
                        }
                      }}
                    />
                    <IconButton
                      sx={{
                        position: 'absolute',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: 'white',
                        pointerEvents: 'none',
                      }}
                    >
                      <Icon.Play sx={{ color: 'primary.main' }} />
                    </IconButton>
                  </>
                ) : (
                  // Image thumbnail
                  <Image
                    src={item.src}
                    alt="Review Image"
                    sx={{
                      borderRadius: '8px',
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                  />
                )}
              </Box>
            </SwiperSlide>
          ))}
          <StyledPagination className="swiper-pagination-custom"></StyledPagination>
        </Swiper>
      </Box>
    </Box>
  )
}
